import { QuickMoveRawEntry } from '../QuickMove/QuickMoveEntry';
import { useScheduleActionLock } from '../../../hook/useScheduleActionLock';
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Col, getItemWithExpiration, Row } from '../../../lib/utils';
import { Body2Bold, Body3Regular, Subtitle1Bold } from '../../../lib/font';
import { colors } from '../../../lib/colors';
import { I_POPUP_EXIT, I_SCHEDULE_CIRCLEX } from '../../../types/images';
import dayjs from 'dayjs';
import NormalToggleButton from '../../ToggleButton/NormalToggleButton';
import {
    deleteScheduleAPI,
    modifyScheduleAttendance,
    updateScheduleSessionAPI,
    deleteRepeatScheduleAPI,
} from '../../../api/schedule';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import ModalUpdateScheduleSession from './ModalUpdateScheduleSession';
import ModalNoShowAlert, { NoShowNoticeType } from '../ModalNoShowAlert';
import { useNavigate } from 'react-router-dom';
import Modal from '../../modal/Modal';
import {
    resolveHistoryCoachId,
    resolveHistoryMemberId,
    resolveHistoryScheduleId,
} from '../../../lib/scheduleHistory';
import MemberQuickInfoCard from './MemberQuickInfoCard';
import { usePublicMemberDetail } from '../../../features/publicMember/hooks';
import {
    hasExplicitScheduleAppAccountStatus,
    isWithdrawnScheduleReadOnly,
} from '../../../lib/scheduleMemberLink';

const ModalOverLay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: #00000047;
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 2000;
`;

const ModalInner = styled.div`
    background: #f5f9ff;
    width: 928px;
    height: 564px;
    border-radius: 8px;
    box-shadow: 8px 8px 32px 0px #0000007a;

    background-color: #fdfeff;
    display: flex;
    flex-direction: column;
`;

const HoverImage = styled.img`
    cursor: pointer;
    &:hover {
        opacity: 0.8;
    }
`;

type Props = {
    modalVisible: boolean;
    setModalVisible: Dispatch<SetStateAction<boolean>>;
    detailObject: ScheduleDetailData;
    createAlert: any;
    createTwoButtonAlert: any;
    calendarType: 'daily' | 'weekly';
    calendarSelectedDay?: string;
    isReadOnly?: boolean;
};

export type ScheduleDetailData = {
    memberId?: string;
    _id?: string;
    id?: string;
    startTime?: string;
    endTime?: string;
    coachId?: string;
    coachName?: string;
    alias?: string;
    attendanceStatus?: string;
    currentSession?: number;
    totalSession?: number;
    minSession?: number;
    scheduleBundleId?: string | null;
    isDeleted?: boolean;
    membership?: {
        name?: string;
        status?: string;
        BGColor?: string[];
    };
    [key: string]: any;
};

const ScheduleDetailModal = ({
    modalVisible,
    setModalVisible,
    detailObject,
    createAlert,
    createTwoButtonAlert,
    calendarType,
    calendarSelectedDay,
    isReadOnly: isReadOnlyProp = false,
}: Props) => {
    const [authObject, setAuthObject] = useState(getItemWithExpiration('authObject'));
    const [authorization, setAuthorization] = useState(getItemWithExpiration('authorization'));
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    /*
     * 스케줄 동작 잠금(설계 §6). 이 모달은 스케줄·회원 상세·수강권 관리 세 화면에 붙는다 —
     * 버튼마다 걸어 두면 세 화면이 함께 덮인다.
     *  수정: 「수정하기」·출석/노쇼(당일취소 문구 포함)·회차 수정·「회원 추가하기」
     *  삭제: 「일정 삭제」(이번 수업만·모두)
     */
    const editLock = useScheduleActionLock('schedule.edit');
    const deleteLock = useScheduleActionLock('schedule.delete');
    const isGoingSchedule = dayjs().isAfter(detailObject?.startTime) && dayjs().isBefore(detailObject?.endTime);
    const [attendState, setAttendState] = useState<string>('ATTENDANCE');
    const [scheduleObject, setScheduleObject] = useState(detailObject);
    const [isMemberQuickInfoVisible, setIsMemberQuickInfoVisible] = useState(false);
    // 수업 일정 회차 정보 수정 모달
    const [isUpdateScheduleSessionVisible, setIsUpdateScheduleSessionVisible] = useState(false);
    // 노쇼 경고 모달
    const [isShowNoShowAlert, setIsShowNoShowAlert] = useState(false);
    // 수업 일정 회차
    const [currentSession, setCurrentSession] = useState<number>(detailObject?.currentSession ?? 0);
    const isDeleted = detailObject.isDeleted;
    const memberId = scheduleObject?.memberId || resolveHistoryMemberId(scheduleObject);
    const isMemberInfoDisabled = isDeleted || !memberId;
    const shouldLoadMemberAccountStatus =
        !!memberId && !hasExplicitScheduleAppAccountStatus(scheduleObject);
    const memberAccountQuery = usePublicMemberDetail({
        centerId: authObject?.centerId || '',
        memberId: memberId || '',
        enabled: modalVisible && shouldLoadMemberAccountStatus,
    });
    const isMemberAccountStatusPending =
        shouldLoadMemberAccountStatus && memberAccountQuery.isLoading;
    const isMemberAccountStatusUnavailable =
        shouldLoadMemberAccountStatus && memberAccountQuery.isError;
    const isReadOnly =
        isReadOnlyProp ||
        isWithdrawnScheduleReadOnly(scheduleObject) ||
        isWithdrawnScheduleReadOnly(memberAccountQuery.data?.centerMember) ||
        isMemberAccountStatusPending ||
        isMemberAccountStatusUnavailable;
    const readOnlyMessage = isMemberAccountStatusPending
        ? '회원 상태를 확인하고 있습니다.'
        : isMemberAccountStatusUnavailable
        ? '회원 상태를 확인할 수 없어 수정 기능을 숨겼습니다.'
        : '탈퇴 회원의 일정은 조회만 가능합니다.';

    // 출석 노쇼 변환 API : (patch)
    const updateScheduleAttendance = useMutation({
        mutationFn: async (noShowNoticeType?: NoShowNoticeType) =>
            await modifyScheduleAttendance(
                scheduleObject?._id || scheduleObject?.id || '',
                attendState === 'ATTENDANCE' ? 'NOSHOW' : 'ATTENDANCE',
                authorization,
                noShowNoticeType
            ),
        onSuccess: (res) => {
            if (res.status === 200) {
                setAttendState(res.data.schedule.attendanceStatus);
                setScheduleObject((current) => ({ ...current, attendanceStatus: res.data.schedule.attendanceStatus }));
                queryClient.invalidateQueries({ queryKey: ['getDailyScheduleAPI'] });
                queryClient.invalidateQueries({ queryKey: ['getWeeklyScheduleAPI'] });
                queryClient.invalidateQueries({ queryKey: ['getSmartRangeSchedulesAPI'] });
                queryClient.invalidateQueries({ queryKey: ['getListRangeSchedulesAPI'] });
                queryClient.invalidateQueries({ queryKey: ['getMemberScheduleHistoryQuery'] });
                queryClient.invalidateQueries({ queryKey: ['getMembershipForMemberQuery'] });
            }
            if (res.status === 400) {
                createAlert(
                    '오류',
                    attendState === 'ATTENDANCE'
                        ? '노쇼 처리에 실패했어요.\n잠시 후 다시 시도해주세요.'
                        : '출석 처리에 실패했어요.\n잠시 후 다시 시도해주세요.',
                    true
                );
            }

            if (res.status === 410 || res.status === 420 || res.status === 430) {
                createAlert('중복', '해당 시간에 이미 스케줄이 등록되어있습니다!\n다시 한번 확인해주세요!', true);
            }
        },
        onError: () => {
            createAlert('오류', '오류가 발생하였습니다!\n다시 한번 등록해주세요!', true);
        },
    });

    // 스케줄 삭제 API : (delete)
    const deleteSchedule = useMutation({
        mutationFn: async () =>
            await deleteScheduleAPI(
                authObject?.centerId,
                scheduleObject?._id || scheduleObject?.id || '',
                scheduleObject?.coachId || '',
                authorization
            ),
        onSuccess: (res) => {
            if (res.status === 204) {
                queryClient.invalidateQueries({ queryKey: ['getDailyScheduleAPI'] });
                queryClient.invalidateQueries({ queryKey: ['getWeeklyScheduleAPI'] });
                queryClient.invalidateQueries({ queryKey: ['getSmartRangeSchedulesAPI'] });
                queryClient.invalidateQueries({ queryKey: ['getListRangeSchedulesAPI'] });
                queryClient.invalidateQueries({ queryKey: ['getMembershipForMemberQuery'] });
                createAlert('성공', '일정이 삭제되었습니다!', false);
            } else {
                createAlert('오류', '오류가 발생하였습니다!\n다시 한번 삭제해주세요!', true);
            }
        },
        onError: () => {
            createAlert('', '오류가 발생하였습니다!\n다시 한번 삭제해주세요!', true);
        },
    });

    // 스케줄 반복 삭제 API : (delete)
    const deleteRepeatSchedule = useMutation({
        mutationFn: async () =>
            await deleteRepeatScheduleAPI(
                authObject?.centerId,
                scheduleObject?._id || scheduleObject?.id || '',
                scheduleObject?.scheduleBundleId || '',
                authorization
            ),
        onSuccess: (res) => {
            if (res.status === 204) {
                queryClient.invalidateQueries({ queryKey: ['getDailyScheduleAPI'] });
                queryClient.invalidateQueries({ queryKey: ['getWeeklyScheduleAPI'] });
                queryClient.invalidateQueries({ queryKey: ['getSmartRangeSchedulesAPI'] });
                queryClient.invalidateQueries({ queryKey: ['getListRangeSchedulesAPI'] });
                queryClient.invalidateQueries({ queryKey: ['getMembershipForMemberQuery'] });
            } else {
                createAlert('오류', '오류가 발생하였습니다!\n다시 한번 삭제해주세요!', true);
            }
        },
        onError: () => {
            createAlert('', '오류가 발생하였습니다!\n다시 한번 삭제해주세요!', true);
        },
    });

    // 수강권 회차 수정 API : (patch)
    const updateScheduleSession = useMutation({
        mutationFn: async (value: number) =>
            await updateScheduleSessionAPI(
                resolveHistoryCoachId(scheduleObject) || '',
                resolveHistoryMemberId(scheduleObject) || '',
                resolveHistoryScheduleId(scheduleObject) || '',
                value,
                authorization
            ),
        onSuccess: (res) => {
            if (res.status === 200) {
                setCurrentSession(res.data.currentSession);
                queryClient.invalidateQueries({ queryKey: ['getDailyScheduleAPI'] });
                queryClient.invalidateQueries({ queryKey: ['getWeeklyScheduleAPI'] });
                queryClient.invalidateQueries({ queryKey: ['getSmartRangeSchedulesAPI'] });
                queryClient.invalidateQueries({ queryKey: ['getListRangeSchedulesAPI'] });
                queryClient.invalidateQueries({ queryKey: ['getMemberScheduleHistoryQuery'] });
                queryClient.invalidateQueries({ queryKey: ['getMembershipForMemberQuery'] });
                queryClient.invalidateQueries({ queryKey: ['getAllMembershipForMemberQuery'] });
            } else if (res.status === 210) {
                createAlert(
                    '수강권 회차 수정 실패',
                    `이미 ${scheduleObject.minSession}회 이상의 수업을 진행하셨습니다.\n수업 정보를 수정해주시거나 ${scheduleObject.minSession}회 이상의 값을 입력해주세요!`,
                    true
                );
            } else {
                createAlert('오류', '오류가 발생하였습니다!\n다시 한번 등록해주세요!', true);
            }
        },
        onError: () => {
            createAlert('', '오류가 발생하였습니다!\n다시 한번 등록해주세요!', true);
        },
    });

    // 출석 노쇼버튼 클릭이벤트
    const onClickAttendToggleButton = () => {
        // updateScheduleAttendance.mutate();
        editLock.run(() => {
            if (attendState === 'ATTENDANCE') {
                setIsShowNoShowAlert(true);
            } else {
                updateScheduleAttendance.mutate(undefined);
            }
        });
    };

    // 회차 수정 버튼 클릭이벤트
    const onClickUpdateScheduleButton = (value: number) => {
        if (
            !resolveHistoryCoachId(scheduleObject) ||
            !resolveHistoryMemberId(scheduleObject) ||
            !resolveHistoryScheduleId(scheduleObject)
        ) {
            createAlert('오류', '수업 회차 수정에 필요한 정보가 부족합니다.', true);
            return;
        }

        // 회차 입력 창을 열어 둔 사이 인증이 만료됐을 수 있다 — 보내기 직전에 다시 본다
        editLock.run(() => updateScheduleSession.mutate(value));
    };

    // 일정 삭제 버튼 클릭이벤트
    const onClickDeleteButton = () => {
        // 인증을 통과한 **뒤에** 모달을 닫는다(취소하면 모달이 그대로 남는다)
        deleteLock.run(() => {
            deleteSchedule.mutate();
            setModalVisible(false);
        });
    };

    // 일정 삭제 버튼 클릭이벤트
    const onClickRepeatDeleteButton = () => {
        deleteLock.run(() => {
            deleteRepeatSchedule.mutate();
            setModalVisible(false);
        });
    };

    useEffect(() => {
        if (scheduleObject) {
            setAttendState(scheduleObject?.attendanceStatus || 'ATTENDANCE');
        }
    }, [scheduleObject]);

    useEffect(() => {
        if (modalVisible) {
            // 모달이 열릴 때 body의 스크롤을 막음
            document.body.style.overflow = 'hidden';
        } else {
            // 모달이 닫힐 때 body의 스크롤을 복원
            document.body.style.overflow = 'auto';
        }

        // 컴포넌트가 언마운트될 때 스크롤을 복원
        return () => {
            document.body.style.overflow = 'auto';
        };
    }, [modalVisible]);

    return (
        <Modal setIsVisible={setModalVisible} isCloseButton whiteViewWidth={960}>
            <Row>
                <Subtitle1Bold style={{ color: colors.LAVEL_4, marginLeft: 48, marginTop: 36 }}>
                    {'PT 수업 일정 자세히 보기'}
                </Subtitle1Bold>

                {(() => {
                    const isCompleted = !isGoingSchedule && !dayjs(scheduleObject?.endTime).isAfter(dayjs());
                    const isUpcoming = !isGoingSchedule && dayjs(scheduleObject?.endTime).isAfter(dayjs());
                    const statusCfg = isGoingSchedule
                        ? { label: '● 진행중', bg: '#DCFCE7', color: '#15803D', border: '#BBF7D0' }
                        : isUpcoming
                        ? { label: '예정', bg: '#DBEAFE', color: '#1D4ED8', border: '#BFDBFE' }
                        : { label: '완료', bg: '#F1F5F9', color: '#475569', border: '#E2E8F0' };
                    return (
                        <span
                            style={{
                                marginTop: 40,
                                marginLeft: 12,
                                padding: '5px 12px',
                                borderRadius: 999,
                                fontSize: 12,
                                fontWeight: 700,
                                backgroundColor: statusCfg.bg,
                                color: statusCfg.color,
                                border: `1px solid ${statusCfg.border}`,
                                height: 24,
                                display: 'inline-flex',
                                alignItems: 'center',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {statusCfg.label}
                        </span>
                    );
                })()}
            </Row>

            {/* ─── 정보 그리드 (2열) ─── */}
            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    columnGap: 60,
                    rowGap: 22,
                    marginTop: 32,
                    marginLeft: 72,
                    marginRight: 72,
                }}
            >
                {/* 담당자 */}
                <Col style={{ gap: 6 }}>
                    <span style={{ fontSize: 12, color: '#6B7280', fontWeight: 500 }}>담당자</span>
                    <span style={{ fontSize: 15, color: '#111827', fontWeight: 700 }}>
                        {scheduleObject?.coachName} 강사
                    </span>
                </Col>

                {/* 날짜 */}
                <Col style={{ gap: 6 }}>
                    <span style={{ fontSize: 12, color: '#6B7280', fontWeight: 500 }}>날짜</span>
                    <span style={{ fontSize: 15, color: '#111827', fontWeight: 700 }}>
                        {dayjs(scheduleObject?.startTime).format('YYYY년 M월 D일 (ddd)')}
                    </span>
                </Col>

                {/* 회원 */}
                <Col style={{ gap: 6 }}>
                    <span style={{ fontSize: 12, color: '#6B7280', fontWeight: 500 }}>회원</span>
                    <Row style={{ alignItems: 'center', gap: 10 }}>
                        <span style={{ fontSize: 15, color: '#111827', fontWeight: 700 }}>
                            {scheduleObject?.alias} 회원
                        </span>
                        <button
                            type="button"
                            disabled={isMemberInfoDisabled}
                            aria-expanded={isMemberQuickInfoVisible}
                            onClick={() => setIsMemberQuickInfoVisible((visible) => !visible)}
                            style={{
                                padding: 0,
                                border: 0,
                                background: 'transparent',
                                color: isMemberInfoDisabled ? '#9CA3AF' : '#2563EB',
                                fontSize: 12,
                                fontWeight: 700,
                                cursor: isMemberInfoDisabled ? 'not-allowed' : 'pointer',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {isMemberQuickInfoVisible ? '정보 닫기' : '회원 정보 보기'}
                        </button>
                    </Row>
                    {isMemberInfoDisabled && (
                        <span style={{ color: '#9CA3AF', fontSize: 11 }}>
                            {isDeleted ? '삭제된 회원의 정보는 조회할 수 없습니다.' : '회원 식별 정보가 없습니다.'}
                        </span>
                    )}
                </Col>

                {/* 시간 */}
                <Col style={{ gap: 6 }}>
                    <span style={{ fontSize: 12, color: '#6B7280', fontWeight: 500 }}>시간</span>
                    <span style={{ fontSize: 15, color: '#111827', fontWeight: 700 }}>
                        {dayjs(scheduleObject?.startTime).format('A h:mm')} ~{' '}
                        {dayjs(scheduleObject?.endTime).format('A h:mm')}
                    </span>
                    <QuickMoveRawEntry item={scheduleObject} onOpenSheet={() => setModalVisible(false)} />
                </Col>

                {/* 수강권 */}
                <Col style={{ gap: 6 }}>
                    <span style={{ fontSize: 12, color: '#6B7280', fontWeight: 500 }}>수강권</span>
                    <span
                        style={{
                            alignSelf: 'flex-start',
                            padding: '5px 12px',
                            borderRadius: 8,
                            fontSize: 13,
                            fontWeight: 700,
                            color: scheduleObject?.membership?.status === 'active' ? '#FFFFFF' : '#4B5563',
                            background:
                                scheduleObject?.membership?.status === 'active' &&
                                scheduleObject?.membership?.BGColor
                                    ? `linear-gradient(135deg, ${scheduleObject.membership.BGColor[0]} 0%, ${scheduleObject.membership.BGColor[1]} 100%)`
                                    : '#E5E7EB',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {scheduleObject?.membership?.name || '-'}
                    </span>
                </Col>

                {/* 회차 정보 */}
                <Col style={{ gap: 6 }}>
                    <span style={{ fontSize: 12, color: '#6B7280', fontWeight: 500 }}>회차 정보</span>
                    <Row style={{ alignItems: 'center', gap: 10 }}>
                        <span style={{ fontSize: 15, color: '#111827', fontWeight: 700 }}>
                            {currentSession ? currentSession : '?'}
                            <span style={{ color: '#9CA3AF', fontWeight: 500 }}>
                                /{scheduleObject?.totalSession ? scheduleObject?.totalSession : '?'}회차
                            </span>
                        </span>
                        {!isDeleted && !isReadOnly && (
                            <button
                                onClick={() => editLock.run(() => setIsUpdateScheduleSessionVisible(true))}
                                style={{
                                    padding: '4px 10px',
                                    height: 26,
                                    borderRadius: 6,
                                    border: '1px solid #D1D5DB',
                                    backgroundColor: '#FFFFFF',
                                    color: '#4B5563',
                                    fontSize: 12,
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                }}
                            >
                                수정
                            </button>
                        )}
                    </Row>
                </Col>
            </div>

            {memberId && !isDeleted && (
                <MemberQuickInfoCard
                    memberId={memberId}
                    visible={isMemberQuickInfoVisible}
                    onOpenFullProfile={() =>
                        window.open(`/publicMember/${encodeURIComponent(memberId)}`, '_blank', 'noopener,noreferrer')
                    }
                />
            )}

            <Row
                style={{
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginTop: isMemberQuickInfoVisible ? 28 : 60,
                    marginBottom: 24,
                    marginLeft: 72,
                    marginRight: 48,
                }}
            >
                {!isReadOnly && <Row style={{ alignItems: 'center' }}>
                    <Body3Regular style={{ color: colors.LAVEL_4, marginRight: 8 }}>{'출석'}</Body3Regular>
                    <NormalToggleButton
                        isActive={attendState === 'ATTENDANCE'}
                        callBack={onClickAttendToggleButton}
                        disableColor="#FF3E4A"
                    />
                    <Body3Regular style={{ color: colors.LAVEL_4, marginLeft: 8 }}>{'노쇼'}</Body3Regular>
                </Row>}
                {isReadOnly ? (
                    <span style={{ color: '#92400E', fontSize: 13, fontWeight: 700 }}>
                        {readOnlyMessage}
                    </span>
                ) : isDeleted ? (
                    <Row style={{ alignItems: 'center', gap: 16 }}>
                        <Row style={{ alignItems: 'center' }}>
                            <img src={I_SCHEDULE_CIRCLEX} style={{ width: 24, height: 24, marginRight: 4 }} />
                            <Body3Regular>삭제된 회원이에요!</Body3Regular>
                        </Row>
                        <button
                            onClick={() => deleteLock.run(() => {
                                return scheduleObject.scheduleBundleId === null
                                    ? createTwoButtonAlert(
                                          '수업을 삭제하시겠습니까?',
                                          `수업을 삭제하시면 회원님께 수업 취소 알림이 전달되며,\n해당 수강권 회차도 수정됩니다! `,
                                          true,
                                          '취소',
                                          '삭제',
                                          () => {},
                                          () => {
                                              onClickDeleteButton();
                                          }
                                      )
                                    : createTwoButtonAlert(
                                          '반복 수업 일정이에요!',
                                          `반복 수업으로 생성된 수업을 일괄 삭제하시겠습니까?\n수업을 삭제하시면 해당 수강권의 회차도 수정됩니다! `,
                                          true,
                                          '이번 수업만 삭제',
                                          '모두 삭제',
                                          () => {
                                              onClickDeleteButton();
                                          },
                                          () => {
                                              onClickRepeatDeleteButton();
                                          },
                                          true
                                      );
                            })}
                            style={{
                                height: 44,
                                minWidth: 100,
                                padding: '0 20px',
                                borderRadius: 10,
                                fontSize: 14,
                                fontWeight: 600,
                                backgroundColor: '#FFFFFF',
                                color: '#DC2626',
                                border: '1px solid #FECACA',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            일정 삭제
                        </button>
                    </Row>
                ) : (
                    <Row style={{ alignItems: 'center', gap: 10 }}>
                        <button
                            onClick={() =>
                                // 회원 구성 변경 = 수정. 여기서 물으면 취소해도 보던 모달에 남는다(경로 가드도 같은 키로 본다)
                                editLock.run(() =>
                                    navigate('/schedule/create', {
                                        state: {
                                            type: 'oneMoreSchedule',
                                            selectedSchedule: scheduleObject,
                                            calendarType,
                                            calendarSelectedDay,
                                        },
                                    })
                                )
                            }
                            style={{
                                height: 44,
                                minWidth: 132,
                                padding: '0 18px',
                                borderRadius: 10,
                                fontSize: 14,
                                fontWeight: 600,
                                backgroundColor: '#FFFFFF',
                                color: '#4B5563',
                                border: '1px solid #D1D5DB',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 6,
                                whiteSpace: 'nowrap',
                            }}
                        >
                            <span style={{ fontSize: 16, lineHeight: 1 }}>+</span>
                            회원 추가하기
                        </button>

                        <button
                            onClick={() => deleteLock.run(() => {
                                return scheduleObject.scheduleBundleId === null
                                    ? createTwoButtonAlert(
                                          '수업을 삭제하시겠습니까?',
                                          `수업을 삭제하시면 회원님께 수업 취소 알림이 전달되며,\n해당 수강권 회차도 수정됩니다! `,
                                          true,
                                          '취소',
                                          '삭제',
                                          () => {},
                                          () => {
                                              onClickDeleteButton();
                                          }
                                      )
                                    : createTwoButtonAlert(
                                          '반복 수업 일정이에요!',
                                          `반복 수업으로 생성된 수업을 일괄 삭제하시겠습니까?\n수업을 삭제하시면 해당 수강권의 회차도 수정됩니다! `,
                                          true,
                                          '이번 수업만 삭제',
                                          '모두 삭제',
                                          () => {
                                              onClickDeleteButton();
                                          },
                                          () => {
                                              onClickRepeatDeleteButton();
                                          },
                                          true
                                      );
                            })}
                            style={{
                                height: 44,
                                minWidth: 100,
                                padding: '0 20px',
                                borderRadius: 10,
                                fontSize: 14,
                                fontWeight: 600,
                                backgroundColor: '#FFFFFF',
                                color: '#DC2626',
                                border: '1px solid #FECACA',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            일정 삭제
                        </button>
                        <button
                            onClick={() =>
                                editLock.run(() =>
                                    navigate('/schedule/update', {
                                        state: {
                                            type: 'oneSchedule',
                                            scheduleObject: {
                                                _id: scheduleObject._id || scheduleObject.id,
                                                ...scheduleObject,
                                            },
                                            calendarType,
                                            calendarSelectedDay,
                                        },
                                    })
                                )
                            }
                            style={{
                                height: 44,
                                minWidth: 120,
                                padding: '0 24px',
                                borderRadius: 10,
                                fontSize: 14,
                                fontWeight: 700,
                                backgroundColor: '#3B82F6',
                                color: 'white',
                                border: 'none',
                                cursor: 'pointer',
                                transition: 'background-color 0.15s',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            수정하기
                        </button>
                    </Row>
                )}
            </Row>

            {!isReadOnly && isUpdateScheduleSessionVisible && (
                <ModalUpdateScheduleSession
                    onClickUpdateScheduleButton={onClickUpdateScheduleButton}
                    currentSession={currentSession}
                    totalSession={scheduleObject?.totalSession ?? 0}
                    minSession={scheduleObject?.minSession ?? 0}
                    setIsVisible={setIsUpdateScheduleSessionVisible}
                />
            )}
            {!isReadOnly && isShowNoShowAlert && (
                <ModalNoShowAlert
                    setIsVisible={setIsShowNoShowAlert}
                    startTime={scheduleObject?.startTime}
                    onClickNoShow={(noticeType) => {
                        // 노쇼 문구를 고르는 사이 인증이 만료됐을 수 있다 — 보내기 직전에 다시 본다
                        editLock.run(() => {
                            updateScheduleAttendance.mutate(noticeType);
                            setIsShowNoShowAlert(false);
                        });
                    }}
                />
            )}
            {editLock.gate}
            {deleteLock.gate}
        </Modal>
    );
};

export default ScheduleDetailModal;
